import './stream.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatMoney(value) {
  return `£${Number(value || 0).toFixed(2)}`;
}

// Profit reads green when it is a gain and red when it is a loss; every other figure stays white.
function profitTone(value) {
  const n = Number(value || 0);
  if (n > 0) return 'gain';
  if (n < 0) return 'loss';
  return undefined;
}

// ─── MetricCard ───────────────────────────────────────────────────────────────
function MetricCard({ title, value, subtitle, tone }) {
  return (
    <div className="rc-stat srv-metric">
      <span className="rc-stat__label">{title}</span>
      <span className="rc-stat__value" data-tone={tone}>{value}</span>
      <span className="rc-stat__note">{subtitle}</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function StreamMetricsCards({ streamData }) {
  if (!streamData) return null;

  const grossMargin = streamData.revenueWithCost > 0
    ? (streamData.grossProfit / streamData.revenueWithCost) * 100
    : 0;

  const qualifyingRevenue = streamData.allGlobalSchemeItems.reduce(
    (sum, item) => sum + item.salePrice * (item.quantity || 1),
    0
  );

  return (
    <div className="srv-metrics">
      {/* Top metrics row */}
      <div className="rc-stats srv-stats">
        <MetricCard
          title="Total Revenue"
          value={formatMoney(streamData.totalRevenue)}
          subtitle={`${streamData.stats.validSales} valid sales`}
        />
        <MetricCard
          title="Total Cost"
          value={formatMoney(streamData.totalCost)}
          subtitle="Only items with cost prices"
        />
        <MetricCard
          title="Gross Profit"
          value={formatMoney(streamData.grossProfit)}
          subtitle={`${grossMargin.toFixed(1)}% gross margin`}
          tone={profitTone(streamData.grossProfit)}
        />
        <MetricCard
          title="Net Profit"
          value={formatMoney(streamData.netProfit)}
          subtitle={streamData.profitMargin ? `${streamData.profitMargin.toFixed(1)}% profit margin` : 'N/A'}
          tone={profitTone(streamData.netProfit)}
        />
        <MetricCard
          title="Global Scheme"
          value={`${streamData.stats.allGlobalSchemeItems}`}
          subtitle={`${formatMoney(streamData.totalGlobalVAT)} VAT across qualifying items`}
        />
      </div>

      {/* Detail row */}
      <div className="srv-detail-row">
        <section className="rc-panel srv-card">
          <h2 className="srv-card__title">
            Fees & VAT Breakdown
          </h2>
          <div className="srv-fees">
            {[
              { label: 'Whatnot Fees', value: formatMoney(streamData.totalFees) },
              { label: 'Standard VAT', value: formatMoney(streamData.totalStandardVAT) },
              { label: 'Global VAT', value: formatMoney(streamData.totalGlobalVAT) },
            ].map((item) => (
              <div key={item.label} className="srv-fee">
                <span className="rc-stat__label">{item.label}</span>
                <span className="srv-fee__value">{item.value}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rc-panel srv-card">
          <h2 className="srv-card__title">
            Global Scheme Summary
          </h2>
          <div className="srv-rows">
            {[
              ['Qualifying items', streamData.stats.allGlobalSchemeItems],
              ['UK export items', streamData.stats.ukGlobalSchemeItems],
              ['Qualifying revenue', formatMoney(qualifyingRevenue)],
              ['Calculated global VAT', formatMoney(streamData.totalGlobalVAT)],
            ].map(([label, value]) => (
              <div key={label} className="srv-row">
                <span className="srv-row__label">{label}</span>
                <span className="srv-row__value">{value}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
